import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    subject: '',
    message: '',
    priority: 'medium'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const categoryOptions = [
    { value: 'technical', label: 'Техническая поддержка' },
    { value: 'billing', label: 'Вопросы по оплате' },
    { value: 'integration', label: 'Интеграция и API' },
    { value: 'sales', label: 'Продажи и тарифы' },
    { value: 'partnership', label: 'Партнерство' },
    { value: 'other', label: 'Другое' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Низкий - Общий вопрос' },
    { value: 'medium', label: 'Средний - Требует внимания' },
    { value: 'high', label: 'Высокий - Срочно' },
    { value: 'critical', label: 'Критический - Система не работает' }
  ];

  const handleChange = (e) => {
    let { name, value } = e?.target;
    
    if (successMessage) setSuccessMessage('');
    if (errors?.submit) setErrors(prev => ({ ...prev, submit: '' }));

    // Ограничение ввода для телефона: только цифры, плюс, скобки, пробел, дефис
    if (name === 'phone') {
      value = value.replace(/[^\d+()-\s]/g, '');
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Пожалуйста, введите ваше имя';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Пожалуйста, введите email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Пожалуйста, введите корректный email';
    }

    if (formData?.phone && formData?.phone?.replace(/\D/g, '')?.length < 10) {
      newErrors.phone = 'Пожалуйста, введите полный номер телефона';
    }

    if (!formData?.category) {
      newErrors.category = 'Пожалуйста, выберите категорию';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Пожалуйста, введите тему обращения';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Пожалуйста, опишите ваш вопрос';
    } else if (formData?.message?.trim()?.length < 20) {
      newErrors.message = 'Сообщение должно содержать минимум 20 символов';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSuccessMessage('');
    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Ошибка сервера');

      setIsSubmitting(false);
      setSuccessMessage('Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        category: '',
        subject: '',
        message: '',
        priority: 'medium'
      });
      
      // Скролл вверх формы для показа сообщения
      window.scrollTo({ top: document.querySelector('.bg-card').offsetTop - 100, behavior: 'smooth' });
    } catch (error) {
      console.error('Error sending email:', error);
      setIsSubmitting(false);
      setErrors(prev => ({ ...prev, submit: 'Произошла ошибка при отправке сообщения. Пожалуйста, попробуйте позже.' }));
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
              Отправьте нам сообщение
            </h2>
            <p className="text-lg text-muted-foreground">
              Заполните форму ниже, и наша команда свяжется с вами в течение 2 часов
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 shadow-lg">
            {successMessage && (
              <div className="mb-8 p-6 bg-green-500/10 border border-green-500/30 rounded-xl flex items-start space-x-4 animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="mt-0.5 text-green-600 dark:text-green-400">
                  <Icon name="CheckCircle" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-1">Успешно!</h4>
                  <p className="text-green-600/90 dark:text-green-400/90">{successMessage}</p>
                </div>
              </div>
            )}
            
            {errors?.submit && (
              <div className="mb-8 p-6 bg-destructive/10 border border-destructive/30 rounded-xl flex items-start space-x-4 animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="mt-0.5 text-destructive">
                  <Icon name="AlertCircle" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-destructive mb-1">Ошибка отправки</h4>
                  <p className="text-destructive/90">{errors.submit}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Полное имя"
                  type="text"
                  name="name"
                  placeholder="Иван Петров"
                  value={formData?.name}
                  onChange={handleChange}
                  error={errors?.name}
                  required
                />

                <Input
                  label="Email адрес"
                  type="email"
                  name="email"
                  placeholder="ivan@company.kz"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Телефон"
                  type="tel"
                  name="phone"
                  placeholder="+7 (777) 123-45-67"
                  value={formData?.phone}
                  onChange={handleChange}
                />

                <Input
                  label="Компания"
                  type="text"
                  name="company"
                  placeholder="Название компании"
                  value={formData?.company}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Категория обращения"
                  options={categoryOptions}
                  value={formData?.category}
                  onChange={(value) => handleSelectChange('category', value)}
                  placeholder="Выберите категорию"
                  error={errors?.category}
                  required
                />

                <Select
                  label="Приоритет"
                  options={priorityOptions}
                  value={formData?.priority}
                  onChange={(value) => handleSelectChange('priority', value)}
                />
              </div>

              <Input
                label="Тема обращения"
                type="text"
                name="subject"
                placeholder="Кратко опишите вашу проблему или вопрос"
                value={formData?.subject}
                onChange={handleChange}
                error={errors?.subject}
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Подробное описание <span className="text-destructive">*</span>
                </label>
                <textarea
                  name="message"
                  rows="6"
                  placeholder="Опишите ваш вопрос или проблему максимально подробно. Укажите шаги для воспроизведения, если это техническая проблема."
                  value={formData?.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-200 ${
                    errors?.message ? 'border-destructive' : 'border-border'
                  }`}
                ></textarea>
                {errors?.message && (
                  <p className="mt-2 text-sm text-destructive flex items-center space-x-1">
                    <Icon name="AlertCircle" size={16} />
                    <span>{errors?.message}</span>
                  </p>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Отправляя эту форму, вы соглашаетесь с нашей политикой конфиденциальности. Мы обрабатываем ваши данные в соответствии с законодательством Республики Казахстан.
                </p>
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
              >
                {isSubmitting ? 'Подготовка...' : 'Отправить сообщение'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;